// src/data/pricing.js
// Updated with Document Limits, Agent Access for ALL, Video Upload, Discover Listing

// ============================================================
// NIGERIA MODE - ₦ Pricing
// ============================================================

export const NIGERIA_PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'forever',
    icon: 'Zap',
    tagline: 'Test the platform with basic documents',
    features: [
      '3 Nigerian compliance documents',
      'Basic governance scoring',
      'Document vault',
      'Email support',
      'Request agent assistance',
    ],
    documentLimit: 3,
    unlockedCategories: [1],
    staffSeats: 0,
    agentAccess: true,           // ✅ ALL plans have agent access
    videoUpload: false,          // ❌ No video upload
    discoverListing: false,      // ❌ Not listed on Discover
    sectorAccess: false,
    highlighted: false,
  },
  {
    id: 'seed',
    name: 'Seed',
    price: 15000,
    period: 'month',
    icon: 'Zap',
    tagline: 'For early-stage startups getting investor ready',
    features: [
      'All 24 Nigerian compliance documents',
      'Full governance scoring',
      'Document vault',
      '1 staff member',
      'Request agent assistance',
      'Email support',
    ],
    documentLimit: 24,
    unlockedCategories: [1, 2],
    staffSeats: 1,
    agentAccess: true,           // ✅ ALL plans have agent access
    videoUpload: false,          // ❌ No video upload
    discoverListing: false,      // ❌ Not listed on Discover
    sectorAccess: false,
    highlighted: false,
  },
  {
    id: 'growth',
    name: 'Growth',
    price: 45000,
    period: 'month',
    icon: 'Star',
    tagline: 'For companies preparing for due diligence and raising capital',
    features: [
      'Everything in Seed, plus:',
      'Unlimited documents',
      'Sector-specific documents',
      '5 staff members',
      'Video pitch upload',
      'Discover page listing',
      'Agent marketplace access',
      'Priority support',
    ],
    documentLimit: Infinity,
    unlockedCategories: [1, 2, 3],
    staffSeats: 5,
    agentAccess: true,           // ✅ ALL plans have agent access
    videoUpload: true,           // ✅ Can upload video pitch
    discoverListing: true,       // ✅ Listed on Discover
    sectorAccess: true,
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 150000,
    period: 'month',
    icon: 'Building2',
    tagline: 'For established companies with complex compliance needs',
    features: [
      'Everything in Growth, plus:',
      'Unlimited staff',
      'White-glove agent service (WE do everything)',
      'Monthly compliance reminders',
      'Tax filing & annual returns',
      'Dedicated account manager',
      'SLA guarantee',
    ],
    documentLimit: Infinity,
    unlockedCategories: [1, 2, 3, 4, 5],
    staffSeats: Infinity,
    agentAccess: 'white-glove',  // ✅ YOU do everything for them
    videoUpload: true,           // ✅ Can upload video pitch
    discoverListing: true,       // ✅ Featured on Discover
    sectorAccess: true,
    highlighted: false,
  },
]

// ============================================================
// INTERNATIONAL MODE - USD Pricing
// ============================================================

export const INTERNATIONAL_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 0,
    period: 'forever',
    icon: 'Zap',
    tagline: 'Test the platform with basic documents',
    features: [
      '3 compliance documents',
      'Basic governance scoring',
      'Document vault',
      'Email support',
      'Request agent assistance',
    ],
    documentLimit: 3,
    unlockedCategories: [1],
    staffSeats: 0,
    agentAccess: true,
    videoUpload: false,
    discoverListing: false,
    sectorAccess: false,
    highlighted: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 49,
    period: 'month',
    icon: 'Star',
    tagline: 'For SMEs and growing startups',
    features: [
      'Everything in Starter, plus:',
      'Unlimited documents',
      'Sector-specific documents',
      '5 staff members',
      'Video pitch upload',
      'Discover page listing',
      'Priority support',
    ],
    documentLimit: Infinity,
    unlockedCategories: [1, 2, 3],
    staffSeats: 5,
    agentAccess: true,
    videoUpload: true,
    discoverListing: true,
    sectorAccess: true,
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 149,
    period: 'month',
    icon: 'Building2',
    tagline: 'For established companies',
    features: [
      'Everything in Professional, plus:',
      'Unlimited staff',
      'White-glove agent service',
      'Dedicated account manager',
      'SLA guarantee',
    ],
    documentLimit: Infinity,
    unlockedCategories: [1, 2, 3, 4, 5],
    staffSeats: Infinity,
    agentAccess: 'white-glove',
    videoUpload: true,
    discoverListing: true,
    sectorAccess: true,
    highlighted: false,
  },
]

// ============================================================
// HELPERS
// ============================================================

export function getPlans(country = 'Nigeria') {
  const plans = country === 'Nigeria' ? NIGERIA_PLANS : INTERNATIONAL_PLANS
  return plans || []
}

export function getPlan(planId, country = 'Nigeria') {
  const plans = getPlans(country)
  return plans.find((p) => p.id === planId) || plans[0] || null
}

export function getPlanPrice(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.price || 0
}

export function getCurrency(country = 'Nigeria') {
  return country === 'Nigeria' ? 'NGN' : 'USD'
}

export function getCurrencySymbol(country = 'Nigeria') {
  return country === 'Nigeria' ? '₦' : '$'
}

// ✅ Document limit helpers
export function getDocumentLimit(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.documentLimit || 3
}

export function getMaxDocuments(planId, country = 'Nigeria') {
  const limit = getDocumentLimit(planId, country)
  return limit === Infinity ? 'Unlimited' : limit
}

// ✅ Feature access helpers
export function hasAgentAccess(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.agentAccess || false
}

export function hasVideoUpload(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.videoUpload || false
}

export function hasDiscoverListing(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.discoverListing || false
}

export function hasSectorAccess(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.sectorAccess || false
}

export function isWhiteGlove(planId, country = 'Nigeria') {
  const plan = getPlan(planId, country)
  return plan?.agentAccess === 'white-glove'
}

// ✅ Category access
export const CATEGORY_ACCESS = {
  nigeria: {
    free: [1],
    seed: [1, 2],
    growth: [1, 2, 3],
    enterprise: [1, 2, 3, 4, 5],
  },
  international: {
    starter: [1],
    professional: [1, 2, 3],
    enterprise: [1, 2, 3, 4, 5],
  }
}

export function getCategoryAccess(planId, country = 'Nigeria') {
  const mode = country === 'Nigeria' ? 'nigeria' : 'international'
  const access = CATEGORY_ACCESS[mode]
  return access[planId] || [1]
}

export const DOC_CATEGORIES = [
  { id: 1, name: 'Corporate & Structure', icon: 'Building2' },
  { id: 2, name: 'Core Financials', icon: 'Wallet' },
  { id: 3, name: 'Team & Human Capital', icon: 'Users' },
  { id: 4, name: 'Risk, Compliance & ESG', icon: 'ShieldCheck' },
  { id: 5, name: 'Legal & Protection', icon: 'Scale' },
]

// Backwards compatibility
export const PLANS = NIGERIA_PLANS